import type { LoginMode } from '@/hooks/useLoginFlow'

/**
 * Work out what a customer typed into the single sign-in field (FEDEV-528).
 *
 * One field accepts an email, a mobile number or a customer number. This
 * decides which it is, so the existing three-field API contract is unchanged:
 * the caller still sends one named field, this just picks it.
 *
 * The classification only routes the value. It is never the validation: the
 * server re-checks a mobile with libphonenumber and looks up the customer
 * number canonically, so a wrong guess degrades to an honest "we couldn't find
 * you" rather than accepting something invalid. The three shapes do not
 * overlap in the real data, so a wrong guess is very unlikely:
 *
 *   - an email contains "@"
 *   - a UK phone number is all digits and starts 0, +44 or 44
 *   - a customer number is 1000000-style or an alphanumeric ChargeBee id,
 *     neither of which starts 0 or +
 *
 * "Phone" rather than "mobile": a landline is routed here too, because the
 * server can tell a landline from a mobile and can then say which problem it
 * is. Deciding that here would mean a second, hand-rolled copy of a judgement
 * libphonenumber already makes on the server, and the two would drift.
 *
 * The bare "7" case matters: people drop the leading zero often enough that
 * without it 7809472800 was classified as a customer number, looked up as one,
 * and honestly reported as not found.
 *
 * What keeps that rule safe is the LENGTH, not the leading digit. Numeric
 * customer numbers do start with 7 sometimes (49 of them across the CRM), but
 * every one of the 3,858 numeric customer numbers is at most 7 digits, and the
 * 1,142 ChargeBee ids are 12-17 characters and never all-numeric. A
 * trunk-less mobile is exactly 10 digits, so nothing real lands in both.
 * Widening this to `^7\d+$` would break that and start swallowing customer
 * numbers, so keep the count exact.
 */
export interface ClassifiedIdentifier {
  mode: LoginMode
  email: string
  mobile: string
  customerNumber: string
}

const EMPTY = { email: '', mobile: '', customerNumber: '' }

export function classifyIdentifier(raw: string): ClassifiedIdentifier {
  const value = raw.trim()

  if (value === '') {
    return { mode: 'email', ...EMPTY }
  }

  if (value.includes('@')) {
    return { mode: 'email', ...EMPTY, email: value }
  }

  // Digits, tolerating the spaces, brackets and dashes people type in numbers.
  // The leading + is kept so an international prefix still reads as a phone.
  const compact = value.replace(/[\s()-]/g, '')
  const digitsOnly = /^\+?\d+$/.test(compact)
  const looksLikePhone =
    digitsOnly &&
    (compact.startsWith('+') ||
      compact.startsWith('07') ||
      compact.startsWith('447') ||
      compact.startsWith('00') ||
      // Any UK number typed with its trunk zero, so 01/02/03 landlines
      // arrive as a phone number too. They are not sign-in-able, but routing
      // one here lets the server recognise it with libphonenumber and say so,
      // instead of looking it up as a customer number and reporting the wrong
      // problem ("we can't find you") for a number we could see was a
      // landline. 07 stays listed separately so a mistyped short mobile is
      // still treated as a phone rather than falling through on length.
      (compact.startsWith('0') && compact.length >= 10) ||
      /^7\d{9}$/.test(compact))

  if (looksLikePhone) {
    return { mode: 'mobile', ...EMPTY, mobile: value }
  }

  return { mode: 'customer_number', ...EMPTY, customerNumber: value }
}
